import { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Table,
  message,
  Checkbox,
  Tag,
  Tooltip,
  Divider,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined, InfoCircleOutlined, PercentageOutlined, UserOutlined } from '@ant-design/icons';
import { supabase } from '../lib/supabase';
import { Product } from '../types';
import { useProductPriceTiers } from '../hooks/useProductPriceTiers';
import PlacesAutocomplete from './PlacesAutocomplete';

interface Client {
  id: string;
  email: string;
  bedrijfsnaam?: string;
}

interface SavedAddress {
  id: string;
  name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  is_default: boolean;
}

interface AdminNewOrderDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (orderData: {
    klant_id: string;
    products: Array<{
      product_id: string;
      hoeveelheid: number;
    }>;
    bezorgadres: string;
    bezorg_postcode: string;
    bezorg_stad: string;
    bezorg_land?: string;
    notities?: string;
    eigen_referentie?: string;
    gewenste_leverdatum?: string;
  }) => Promise<void>;
}

// Helper function to format price as currency
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
};

export default function AdminNewOrderDialog({ open, onClose, onSubmit }: AdminNewOrderDialogProps) {
  const [form] = Form.useForm();
  const [products, setProducts] = useState<Product[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientAddresses, setClientAddresses] = useState<SavedAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<{[key: string]: {product: Product, quantity: number}}>({});
  const [calculatingPrices, setCalculatingPrices] = useState(false);
  const [orderTotal, setOrderTotal] = useState(0);
  
  // Use our custom hook to get tiered pricing data
  const { priceTiers, getPriceForQuantity, loading: tiersLoading } = useProductPriceTiers();

  useEffect(() => {
    fetchProducts();
    fetchClients();
  }, []);

  // Update order total whenever products or quantities change
  useEffect(() => {
    setCalculatingPrices(true);
    // Short delay to allow UI to update
    const timer = setTimeout(() => {
      setCalculatingPrices(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedProducts]);

  // Force UI update when form values change
  const forceUpdate = () => {
    setCalculatingPrices(true);
    // Use a shorter timeout to make the UI more responsive
    setTimeout(() => {
      try {
        const newSelectedProducts = {...selectedProducts};
        setSelectedProducts(newSelectedProducts);
      } finally {
        setCalculatingPrices(false);
      }
    }, 50);
  };

  // Listen for form changes to update the selected products
  useEffect(() => {
    const formProducts = form.getFieldValue('products') || [];
    
    const newSelectedProducts: {[key: string]: {product: Product, quantity: number}} = {};
    
    formProducts.forEach((formProduct: any, index: number) => {
      if (formProduct && formProduct.product_id) {
        const product = products.find(p => p.id === formProduct.product_id);
        if (product) {
          newSelectedProducts[index] = {
            product,
            quantity: formProduct.quantity || 0.01
          };
        }
      }
    });
    
    setSelectedProducts(newSelectedProducts);
  }, [form.getFieldsValue(), products]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      // Get all users from admin_users view
      const { data: authUsersData, error: authUsersError } = await supabase.rpc('admin_get_all_users');
      
      if (authUsersError) throw authUsersError;
      
      // Fetch companies info to get company names
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('user_id, bedrijfsnaam');
        
      if (companiesError) throw companiesError;
      
      // Create a map of user_id to company name
      const companyNameMap: Record<string, string> = {};
      companiesData?.forEach(company => {
        if (company.user_id) {
          companyNameMap[company.user_id] = company.bedrijfsnaam;
        }
      });
      
      // Map auth users to clients with company name if available
      const clientsData = authUsersData.map((user: any) => ({
        id: user.id,
        email: user.email,
        bedrijfsnaam: companyNameMap[user.id] || undefined
      }));
      
      setClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de klanten');
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchClientAddresses = async (clientId: string) => {
    if (!clientId) {
      setClientAddresses([]);
      return;
    }
    
    setLoadingAddresses(true);
    try {
      // First check saved addresses
      const { data, error } = await supabase
        .from('saved_addresses')
        .select('*')
        .eq('user_id', clientId)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      
      setClientAddresses(data || []);
      
      // If no saved addresses, check company profile for address
      if (!data || data.length === 0) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('adres, postcode, stad, land')
          .eq('user_id', clientId)
          .single();
          
        if (companyError) {
          // Just log the error but don't throw, since company might not have a profile
          console.log('No company profile found for client');
          return;
        }
        
        if (companyData && companyData.adres) {
          // Create a virtual saved address from company data
          setClientAddresses([
            {
              id: 'company_address',
              name: 'Bedrijfsadres',
              street: companyData.adres,
              postal_code: companyData.postcode || '',
              city: companyData.stad || '',
              country: companyData.land || 'Nederland',
              is_default: true
            }
          ]);
        }
      }
    } catch (error) {
      console.error('Error fetching client addresses:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de adressen');
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Handle client selection
  const handleClientChange = (clientId: string) => {
    // Reset address related fields
    form.setFieldsValue({
      saved_address: undefined,
      shipping_address: '',
      shipping_postcode: '',
      shipping_city: '',
      shipping_country: 'Nederland'
    });
    
    // Fetch client's addresses
    fetchClientAddresses(clientId);
  };

  // Update the form when the fields change
  const handleProductChange = (value: string, index: number) => {
    const formData = form.getFieldsValue();
    const productData = formData.products || [];
    
    if (productData[index]) {
      productData[index].product_id = value;
      
      // Reset quantity to a very small decimal for sample orders
      productData[index].quantity = 0.01;
      
      form.setFieldsValue({ products: productData });
      updateOrderTotal();
      forceUpdate();
    }
  };

  const handleQuantityChange = (value: number | null, index: number) => {
    const formData = form.getFieldsValue();
    const productData = formData.products || [];
    
    if (productData[index]) {
      productData[index].quantity = value || 0.01; // Default to 0.01 if null
      form.setFieldsValue({ products: productData });
      updateOrderTotal();
      forceUpdate();
    }
  };

  // Helper function to update the order total
  const updateOrderTotal = () => {
    const formProducts = form.getFieldValue('products') || [];
    let total = 0;
    
    formProducts.forEach((item: any) => {
      if (item && item.product_id) {
        const quantity = item.quantity || 0.01;
        const price = getProductPrice(item.product_id, quantity);
        total += price * quantity;
      }
    });
    
    setOrderTotal(total);
  };

  const handleSelectSavedAddress = (addressId: string) => {
    const address = clientAddresses.find(addr => addr.id === addressId);
    if (address) {
      form.setFieldsValue({
        shipping_address: address.street,
        shipping_postcode: address.postal_code,
        shipping_city: address.city,
        shipping_country: address.country,
      });
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('producten')
        .select('*')
        .order('naam');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Er is een fout opgetreden bij het ophalen van de producten');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      if (!values.client_id) {
        message.error('Selecteer een klant');
        return;
      }
      
      const orderProducts = values.products || [];
      
      if (!orderProducts.length) {
        message.error('Voeg minimaal één product toe');
        return;
      }

      await onSubmit({
        klant_id: values.client_id,
        products: orderProducts.map((item: any) => ({
          product_id: item.product_id,
          hoeveelheid: item.quantity || 0.01, // Use quantity as the quantity
        })),
        bezorgadres: values.shipping_address,
        bezorg_postcode: values.shipping_postcode,
        bezorg_stad: values.shipping_city,
        bezorg_land: values.shipping_country,
        notities: values.notes,
        eigen_referentie: values.eigen_referentie,
        gewenste_leverdatum: values.gewenste_leverdatum,
      });

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Error submitting order:', error);
      message.error('Er is een fout opgetreden bij het aanmaken van de order');
    } finally {
      setLoading(false);
    }
  };

  // Calculate the applicable tiered price for a product
  const getProductPrice = (productId: string, quantity: number): number => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;
    
    return getPriceForQuantity(productId, quantity, product.prijs);
  };

  // Get tier information for a product to display in tooltip
  const getProductTierInfo = (productId: string): React.ReactNode => {
    const productTiers = priceTiers.filter(tier => tier.product_id === productId)
      .sort((a, b) => a.min_quantity - b.min_quantity);
    
    if (productTiers.length === 0) return null;
    
    const product = products.find(p => p.id === productId);
    if (!product) return null;
    
    const unit = product.artikel_per || 'stuk';
    
    return (
      <div>
        <p><strong>Staffelprijzen:</strong></p>
        <p>Standaard: {formatPrice(product.prijs)} per {unit}</p>
        {productTiers.map(tier => (
          <p key={tier.id}>
            Vanaf {tier.min_quantity} {unit}: {formatPrice(tier.price)} per {unit}
          </p>
        ))}
      </div>
    );
  };

  const productColumns = [
    {
      title: 'Product',
      dataIndex: 'product_id',
      key: 'product_id',
      width: '25%',
      render: (_: any, record: any, index: number) => (
        <Form.Item
          name={[index, 'product_id']}
          rules={[{ required: true, message: 'Selecteer een product' }]}
          style={{ margin: 0 }}
        >
          <Select 
            style={{ width: '100%' }}
            placeholder="Selecteer een product"
            dropdownStyle={{ width: 'auto', minWidth: '400px' }}
            onChange={(value) => {
              // Update quantity to 0.1 for new product selection
                const formProducts = form.getFieldValue('products') || [];
                if (formProducts[index]) {
                formProducts[index].quantity = 0.01;
                  form.setFieldsValue({ products: formProducts });
              }
              handleProductChange(value, index);
            }}
            optionLabelProp="label"
            showSearch
            filterOption={(input, option) => 
              (option?.title?.toLowerCase() || '').includes(input.toLowerCase()) ||
              (option?.searchvalue?.toLowerCase() || '').includes(input.toLowerCase())
            }
          >
            {products.map((product) => (
              <Select.Option 
                key={product.id} 
                value={product.id}
                title={`${product.naam} - ${product.artikelnummer || 'Geen artikelnr.'}`}
                searchvalue={`${product.naam} ${product.artikelnummer || ''}`}
                label={product.naam}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span style={{ fontWeight: 'bold' }}>{product.naam}</span>
                  <span style={{ color: '#888', marginLeft: '10px' }}>
                    {product.artikelnummer ? `#${product.artikelnummer}` : 'Geen artikelnr.'}
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: 'Artikelnr.',
      key: 'artikelnummer',
      width: 110,
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        return product.artikelnummer || '-';
      }
    },
    {
      title: 'Verkoopeenheid',
      key: 'unit',
      width: 120,
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        const unit = product.artikel_per || 'stuk';
        
        return (
          <Tooltip title={`Dit product wordt verkocht per ${unit}. Voor samples kunt u decimalen gebruiken (bijv. 0.01)`}>
            <span style={{ 
              display: 'inline-block',
              padding: '2px 8px',
              background: '#f5f5f5',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              {unit}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Aantal',
      key: 'quantity',
      width: 120,
      render: (_: any, record: any, index: number) => {
        return (
          <Form.Item
            name={[index, 'quantity']}
            rules={[
              { required: true, message: 'Voer een aantal in' },
              { 
                validator: (_, value) => {
                  if (value < 0.01) {
                    return Promise.reject('Aantal moet minimaal 0.01 zijn');
                  }
                  return Promise.resolve();
                }
              }
            ]}
            style={{ margin: 0 }}
            initialValue={0.01}
          >
            <InputNumber 
              min={0.01} 
              style={{ width: '100px' }}
              placeholder="Aantal"
              step={0.01}
              onChange={(value) => {
                handleQuantityChange(value, index);
              }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: 'Totaal aantal stuks',
      key: 'total_pieces',
      width: 140,
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        const quantity = form.getFieldValue(['products', index, 'quantity']) || 0.1;
        
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        const unit = product.artikel_per || 'stuk';
        
        // Calculate actual pieces for display
        if (unit === '1000 stuks') {
          const totalPieces = quantity * 1000;
          return (
            <Tooltip title={`${quantity} eenheden van ${unit}`}>
              <span>{totalPieces.toLocaleString('nl-NL')} stuks</span>
            </Tooltip>
          );
        } else if (unit === 'kg') {
          return <span>{quantity} kg</span>;
        } else {
          return <span>{quantity} {unit}</span>;
        }
      }
    },
    {
      title: 'Prijs per eenheid',
      key: 'unit_price',
      width: 140,
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        const quantity = form.getFieldValue(['products', index, 'quantity']) || 0.01;
        
        if (!productId) return '-';
        
        const product = products.find(p => p.id === productId);
        if (!product) return '-';
        
        const unit = product.artikel_per || 'stuk';
        const basePrice = product.prijs;
        const actualPrice = getProductPrice(productId, quantity);
        const hasTiers = priceTiers.some(tier => tier.product_id === productId);
        
        // Show discount indicator if tiered pricing is applied
        const hasDiscount = actualPrice < basePrice;
        
        return (
          <div>
            {calculatingPrices && tiersLoading ? (
              <span style={{ opacity: 0.6 }}>Berekenen...</span>
            ) : (
              <>
                <div>
                  {formatPrice(actualPrice)}
                  {hasDiscount && (
                    <Tooltip title="Staffelkorting toegepast">
                      <PercentageOutlined style={{ 
                        marginLeft: 4, 
                        color: '#52c41a', 
                        fontSize: '13px',
                        position: 'relative',
                        top: '-1px' 
                      }} />
                    </Tooltip>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                  per {unit}
                </div>
                {hasTiers && (
                  <Tooltip title={getProductTierInfo(productId)}>
                    <InfoCircleOutlined style={{ 
                      marginLeft: 4, 
                      color: '#1890ff', 
                      fontSize: '12px',
                      position: 'relative',
                      top: '-1px' 
                    }} />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      title: 'Totaal',
      key: 'total',
      width: 120,
      render: (_: any, record: any, index: number) => {
        const productId = form.getFieldValue(['products', index, 'product_id']);
        const quantity = form.getFieldValue(['products', index, 'quantity']) || 0.01;
        
        if (!productId) return '-';
        
        const price = getProductPrice(productId, quantity);
        return calculatingPrices && tiersLoading ? (
          <span style={{ opacity: 0.6 }}>Berekenen...</span>
        ) : (
          <strong>{formatPrice(price * quantity)}</strong>
        );
      }
    },
    {
      title: 'Actie',
      key: 'action',
      width: 80,
      render: (_: any, record: any, index: number, remove: (index: number) => void) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => remove(index)}
        />
      ),
    },
  ];

  // Calculate total price whenever form values change
  useEffect(() => {
    const formProducts = form.getFieldValue('products') || [];
    let total = 0;
    
    formProducts.forEach((item: any) => {
      if (item && item.product_id) {
        const quantity = item.quantity || 0.01;
        const price = getProductPrice(item.product_id, quantity);
        total += price * quantity;
      }
    });
    
    setOrderTotal(total);
  }, [selectedProducts, priceTiers, products]);
  
  // Update summary display
  const renderSummary = () => {
    return (
      <Table.Summary.Row style={{ fontWeight: 'bold' }}>
        <Table.Summary.Cell index={0} colSpan={6}>
          <strong>Order Totaal</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1}>
          <strong style={{ fontSize: '16px', color: '#1890ff' }}>{formatPrice(orderTotal)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2}></Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <Modal
      title="Nieuwe order aanmaken"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1200}
      styles={{
        body: { overflowX: 'auto' }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          products: [],
          shipping_country: 'Nederland',
        }}
      >
        <Divider orientation="left">Klant selecteren</Divider>
        
        <Form.Item
          name="client_id"
          label="Klant"
          rules={[{ required: true, message: 'Selecteer een klant' }]}
        >
          <Select
            placeholder="Selecteer een klant"
            loading={loadingClients}
            showSearch
            filterOption={(input, option) => 
              (option?.label?.toLowerCase() || '').includes(input.toLowerCase())
            }
            onChange={handleClientChange}
            optionLabelProp="label"
            options={clients.map(client => ({
              value: client.id,
              label: client.bedrijfsnaam ? `${client.bedrijfsnaam} (${client.email})` : client.email,
              title: client.bedrijfsnaam ? `${client.bedrijfsnaam} - ${client.email}` : client.email
            }))}
          />
        </Form.Item>
        
        <Divider orientation="left">Bezorggegevens</Divider>

        <Form.Item
          name="saved_address"
          label="Opgeslagen adres"
          style={{ marginBottom: 8 }}
        >
          <Select
            placeholder="Selecteer een opgeslagen adres"
            onChange={handleSelectSavedAddress}
            allowClear
            loading={loadingAddresses}
            disabled={clientAddresses.length === 0}
          >
            {clientAddresses.map((address) => (
              <Select.Option key={address.id} value={address.id}>
                {address.name} - {address.street}, {address.postal_code} {address.city}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="shipping_address"
          label="Bezorgadres"
          rules={[{ required: true, message: 'Voer een bezorgadres in' }]}
        >
          <Input placeholder="Straatnaam en huisnummer" />
        </Form.Item>

        <Form.Item
          name="shipping_postcode"
          label="Postcode"
          rules={[{ required: true, message: 'Voer een postcode in' }]}
        >
          <Input placeholder="Postcode" />
        </Form.Item>

        <Form.Item
          name="shipping_city"
          label="Stad"
          rules={[{ required: true, message: 'Voer een stad in' }]}
        >
          <Input placeholder="Stad" />
        </Form.Item>

        <Form.Item
          name="shipping_country"
          label="Land"
          initialValue="Nederland"
        >
          <Input placeholder="Land" />
        </Form.Item>

        <Divider orientation="left">Order gegevens</Divider>

        <Form.Item
          name="gewenste_leverdatum"
          label="Gewenste leverdatum"
        >
          <Input type="date" />
        </Form.Item>

        <Form.Item 
          name="eigen_referentie" 
          label="Eigen referentie"
          style={{ marginBottom: 24 }}
        >
          <Input placeholder="Voer een referentie in (optioneel)" />
        </Form.Item>

        <Form.Item 
          name="notes" 
          label="Notities"
          style={{ marginBottom: 24 }}
        >
          <Input.TextArea rows={4} placeholder="Optionele notities voor deze bestelling" />
        </Form.Item>

        <Divider orientation="left">Producten</Divider>

        <Form.List name="products">
          {(fields, { add, remove }) => (
            <>
              <Form.Item>
                <Table
                  dataSource={fields as any[]}
                  columns={productColumns.map(col => ({
                    ...col,
                    render: (...args) => col.render?.(...args, remove),
                  }))}
                  pagination={false}
                  summary={renderSummary}
                  scroll={{ x: 'max-content' }}
                  locale={{
                    emptyText: 'Geen producten toegevoegd. Klik op "Product toevoegen" hieronder om te beginnen.'
                  }}
                />
                <Button
                  type="dashed"
                  onClick={() => {
                    add({ quantity: 0.01 });
                    // Wait for the UI to update, then focus on the new select
                    setTimeout(() => {
                      const selects = document.querySelectorAll('.ant-select');
                      if (selects && selects.length > 0) {
                        const lastSelect = selects[selects.length - 1];
                        if (lastSelect) {
                          (lastSelect as HTMLElement).click();
                        }
                      }
                    }, 100);
                  }}
                  style={{ marginTop: 16, marginBottom: 24, width: '100%', height: '40px' }}
                  icon={<PlusOutlined />}
                >
                  Product toevoegen
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>
              Annuleren
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Order aanmaken
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
} 